import { useEffect, useState } from "react";
import { Ban, Check, KeyRound, List, Loader2, Pencil, Play, Plus, RefreshCcw, X } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import AdminApiKey from "./AdminApiKey";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

export default function AdminDealers() {
  const { t, lang } = useLanguage();
  const [dealers, setDealers] = useState(null);
  const [form, setForm] = useState({ company: "", email: "", credit_eur: "", markup_pct: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null); // credenziali mostrate una sola volta
  const [marginEdit, setMarginEdit] = useState(null); // {user_id, value}
  const [logs, setLogs] = useState(null); // {dealer, items|null}

  const load = () => api.get("/admin/dealers").then(({ data }) => setDealers(data.data || [])).catch(() => setDealers([]));
  useEffect(() => { load(); }, []);

  const eur = (v) => { try { return new Intl.NumberFormat(lang, { style: "currency", currency: "EUR" }).format(v || 0); } catch { return `€${(v || 0).toFixed(2)}`; } };

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { company: form.company, email: form.email, credit_eur: parseFloat(form.credit_eur) || 0 };
      if (form.markup_pct !== "" && !Number.isNaN(parseFloat(form.markup_pct))) payload.markup_pct = parseFloat(form.markup_pct);
      const { data } = await api.post("/admin/dealers", payload);
      setCreated(data.data);
      setForm({ company: "", email: "", credit_eur: "", markup_pct: "" });
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (d) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/dealers/${d.user_id}/rotate-key`);
      setCreated({ email: d.email, api_key: data.data.api_key, temp_password: null });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.post(`/admin/dealers/${d.user_id}/toggle`);
      await load();
    } catch {
      toast.error(t("misc.error"));
    }
  };

  const saveMargin = async (d) => {
    const raw = (marginEdit?.value ?? "").trim();
    if (raw !== "" && Number.isNaN(parseFloat(raw))) { toast.error(t("misc.error")); return; }
    try {
      await api.post(`/admin/dealers/${d.user_id}/margin`, { markup_pct: raw === "" ? null : parseFloat(raw) });
      setMarginEdit(null);
      toast.success(t("admin.saved"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("misc.error"));
    }
  };

  const openLogs = async (d) => {
    setLogs({ dealer: d, items: null });
    try {
      const { data } = await api.get(`/admin/dealers/${d.user_id}/logs`);
      setLogs({ dealer: d, items: data.data || [] });
    } catch {
      setLogs({ dealer: d, items: [] });
      toast.error(t("misc.error"));
    }
  };

  const statusCls = (s) => s < 300 ? "bg-emerald-50 text-emerald-700" : s < 500 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-600";

  const inputCls = "rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-[#FF6B35] transition-colors";
  const lbl = "font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 block mb-1.5";

  return (
    <div data-testid="admin-dealers-page" className="mt-10 space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-slate-900">{t("admin.dealers")}</h2>
        <p className="text-sm text-slate-500">{t("dealers.sub")}</p>
      </div>

      <AdminApiKey />

      {created && (
        <div data-testid="dealer-created" className="rounded-3xl bg-emerald-50 border border-emerald-200 p-6">
          <div className="font-display font-bold text-emerald-800 mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" /> {t("dealers.createdTitle")}</div>
          <p className="text-xs text-emerald-700 mb-3">{created.upgraded ? t("dealers.upgradedNote") : t("dealers.createdNote")}</p>
          <div className="space-y-2 font-mono-t text-xs">
            <div data-testid="dealer-created-key" className="rounded-xl bg-white border border-emerald-200 px-4 py-2.5 break-all select-all">API Key: {created.api_key}</div>
            {created.temp_password && (
              <div data-testid="dealer-created-pw" className="rounded-xl bg-white border border-emerald-200 px-4 py-2.5 break-all select-all">Password: {created.temp_password}</div>
            )}
          </div>
        </div>
      )}

      <form onSubmit={create} className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 grid sm:grid-cols-5 gap-3 items-end" data-testid="dealer-create-form">
        <div><label className={lbl}>{t("dealers.company")}</label>
          <input data-testid="dealer-f-company" required minLength={2} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={`${inputCls} w-full`} /></div>
        <div><label className={lbl}>Email</label>
          <input data-testid="dealer-f-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inputCls} w-full`} /></div>
        <div><label className={lbl}>{t("dealers.credit")}</label>
          <input data-testid="dealer-f-credit" type="number" min="0" step="0.01" value={form.credit_eur} onChange={(e) => setForm({ ...form, credit_eur: e.target.value })} className={`${inputCls} w-full`} /></div>
        <div><label className={lbl}>{t("dealers.margin")}</label>
          <input data-testid="dealer-f-margin" type="number" min="0" max="100" step="0.5" placeholder={t("dealers.listino")} value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} className={`${inputCls} w-full`} /></div>
        <button data-testid="dealer-create" type="submit" disabled={busy}
          className="rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} {t("dealers.create")}
        </button>
      </form>

      <p className="font-mono-t text-[10px] text-slate-400 uppercase tracking-widest px-1 -mt-2">{t("dealers.createHint")}</p>

      <div className="rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 overflow-x-auto">
        {dealers === null ? (
          <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
        ) : dealers.length === 0 ? (
          <p data-testid="dealers-empty" className="py-8 text-left text-sm text-slate-400">{t("dealers.empty")}</p>
        ) : (
          <table className="w-full min-w-[860px] text-sm" data-testid="dealers-table">
            <thead>
              <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="pb-2 pr-3">{t("dealers.company")}</th>
                <th className="pb-2 pr-3">API Key</th>
                <th className="pb-2 pr-3 text-right">{t("dealers.margin")}</th>
                <th className="pb-2 pr-3 text-right">{t("dealers.credit")}</th>
                <th className="pb-2 pr-3 text-right">Ordini</th>
                <th className="pb-2 pr-3 text-right">Chiamate API</th>
                <th className="pb-2 text-right">Stato</th>
              </tr>
            </thead>
            <tbody>
              {dealers.map((d) => (
                <tr key={d.user_id} data-testid={`dealer-row-${d.user_id}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-slate-800">{d.dealer_company || d.name}</div>
                    <div className="font-mono-t text-[10px] text-slate-400">{d.email}</div>
                  </td>
                  <td className="py-2.5 pr-3 font-mono-t text-xs text-slate-500">{d.api_key_prefix}</td>
                  <td className="py-2.5 pr-3 text-right">
                    {marginEdit?.user_id === d.user_id ? (
                      <span className="inline-flex items-center justify-end gap-1.5">
                        <input data-testid="dealer-margin-input" type="number" min="0" max="100" step="0.5" autoFocus
                          value={marginEdit.value} onChange={(e) => setMarginEdit({ user_id: d.user_id, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveMargin(d); } if (e.key === "Escape") setMarginEdit(null); }}
                          placeholder={t("dealers.listino")}
                          className="w-20 rounded-xl border border-slate-200 px-2 py-1 text-xs outline-none focus:border-[#FF6B35]" />
                        <button data-testid="dealer-margin-save" onClick={() => saveMargin(d)} title={t("admin.saved")}
                          className="rounded-full border border-slate-200 p-1.5 text-emerald-600 hover:border-emerald-400 transition-colors">
                          <Check className="w-3 h-3" />
                        </button>
                        <button data-testid="dealer-margin-cancel" onClick={() => setMarginEdit(null)}
                          className="rounded-full border border-slate-200 p-1.5 text-slate-400 hover:border-slate-400 transition-colors">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-end gap-1.5">
                        <span data-testid={`dealer-margin-${d.user_id}`} className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${d.dealer_markup_pct != null ? "bg-[#FF6B35]/10 text-[#E8551F]" : "bg-slate-50 text-slate-500"}`}>
                          {d.dealer_markup_pct != null ? `+${d.dealer_markup_pct}%` : t("dealers.listino")}
                        </span>
                        <button data-testid={`dealer-margin-edit-${d.user_id}`} onClick={() => setMarginEdit({ user_id: d.user_id, value: d.dealer_markup_pct ?? "" })} title={t("dealers.marginHint")}
                          className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs font-bold">{eur(d.wallet_balance)}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{d.orders_count ?? 0}</td>
                  <td className="py-2.5 pr-3 text-right font-mono-t text-xs">{(d.dealer_stats || {}).requests ?? 0}</td>
                  <td className="py-2.5 text-right">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[9px] font-bold uppercase tracking-widest ${d.dealer_active !== false ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        {d.dealer_active !== false ? t("dealers.active") : t("dealers.disabled")}
                      </span>
                      <button data-testid={`dealer-logs-${d.user_id}`} onClick={() => openLogs(d)} title={t("dealers.logs")}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <List className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`dealer-rotate-${d.user_id}`} onClick={() => rotate(d)} disabled={busy} title={t("dealers.rotate")}
                        className="rounded-full border border-slate-200 p-2 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors">
                        <RefreshCcw className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`dealer-toggle-${d.user_id}`} onClick={() => toggle(d)} title={t("dealers.toggle")}
                        className={`rounded-full border p-2 transition-colors ${d.dealer_active !== false ? "border-slate-200 text-slate-400 hover:border-red-400 hover:text-red-500" : "border-slate-200 text-slate-400 hover:border-emerald-500 hover:text-emerald-600"}`}>
                        {d.dealer_active !== false ? <Ban className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!logs} onOpenChange={(o) => { if (!o) setLogs(null); }}>
        <DialogContent data-testid="dealer-logs-dialog" className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("dealers.logsTitle")} — {logs?.dealer?.dealer_company || logs?.dealer?.email}</DialogTitle>
            <DialogDescription className="sr-only">{t("dealers.logsTitle")}</DialogDescription>
          </DialogHeader>
          {logs && (logs.items === null ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
          ) : logs.items.length === 0 ? (
            <p data-testid="dealer-logs-empty" className="py-8 text-left text-sm text-slate-400">{t("dealers.logsEmpty")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[540px] text-sm" data-testid="dealer-logs-table">
                <thead>
                  <tr className="text-left font-mono-t text-[9px] font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100">
                    <th className="pb-2 pr-3">Data</th>
                    <th className="pb-2 pr-3">Metodo</th>
                    <th className="pb-2 pr-3">Endpoint</th>
                    <th className="pb-2 pr-3">Esito</th>
                    <th className="pb-2 text-right">ms</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.items.map((l) => (
                    <tr key={l.log_id} data-testid={`dealer-log-${l.log_id}`} className="border-b border-slate-50">
                      <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString(lang)}</td>
                      <td className="py-2 pr-3">
                        <span className={`rounded px-2 py-0.5 font-mono-t text-[10px] font-bold ${l.method === "GET" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>{l.method}</span>
                      </td>
                      <td className="py-2 pr-3 font-mono-t text-xs text-slate-700">{l.path}</td>
                      <td className="py-2 pr-3"><span className={`rounded-full px-2.5 py-0.5 font-mono-t text-[10px] font-bold ${statusCls(l.status)}`}>{l.status}</span></td>
                      <td className="py-2 text-right font-mono-t text-xs text-slate-500">{l.ms}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </div>
  );
}
